// Angular imports
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { map, catchError } from 'rxjs/operators';

// Rxjs import
import { Observable, of } from 'rxjs';

// Service imports
import { ServerErrorService } from '../_services/shared-services/server-error.service';
import { ContextManagerService } from '../_services/data-services/context-manager.service';

// Model imports
import { PropertyCommunityInfoParameterDto } from '../_models/property-community-info-parameter-dto';

@Injectable({
    providedIn: 'root'
})
export class ContextGuard implements CanActivate {
    constructor(
        private readonly errorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        const communityId: string = route.params.communityId;
        const customerId: string = route.params.customerId;
        const stateURL: string = state.url;
        const url = this.getConfiguredUrl(route);

        this.contextManagerService.setSelectedContextRouteItem(url);
        if (communityId || customerId) {
            return this.setApplicationContext(communityId, customerId);
        } else {
            return this.getContextFromStateURL(stateURL);
        }
    }

    private setApplicationContext(communityId, customerId): Observable<boolean> {
        const baseParams: PropertyCommunityInfoParameterDto = new PropertyCommunityInfoParameterDto();
        baseParams.communityId = parseInt(communityId);
        baseParams.customerId = customerId || null;
        baseParams.clearCache = true;

        return this.contextManagerService.getContextInfo(baseParams).pipe(
            map((data) => {
                this.contextManagerService.updateContext(data);
                return true;
            }),
            catchError((error) => {
                this.errorService.displayError(error);
                return of(false);
            })
        );
    }

    private getConfiguredUrl(route: ActivatedRouteSnapshot): string {
        const tabName: string = route.params.tabName;
        let configURL =
            '/' +
            route.pathFromRoot
                .filter((v) => v.routeConfig && !!v.routeConfig.path)
                .map((v) => v.routeConfig.path)
                .join('/');

        if (!!tabName && configURL.endsWith('tabName')) {
            configURL = configURL.replace(':tabName', tabName);
        }
        return configURL;
    }

    private getContextFromStateURL(stateURL: string): Observable<boolean> {
        let communityId: string;
        let customerId: string;

        const tokens = stateURL.split('/');
        for (const token of tokens) {
            if (token.length) {
                if (parseInt(token)) {
                    communityId = token;
                } else if (token.startsWith('R')) {
                    customerId = token;
                }
            }
            if (customerId && communityId) {
                break;
            }
        }

        return this.setApplicationContext(communityId, customerId);
    }
}
